import { Injectable } from '@angular/core';
import { ValidationError, ValidationProfiler } from 'validation';

export class ValidationServiceRegistration {
  path: string;
  pathExact: boolean;
  consumer: (final: boolean, errors: ValidationError[]) => void;
  getElementForFocus?: () => HTMLElement | null;
}

@Injectable()
export class ValidationService {
  private validator: () => ValidationError[] = () => [];
  private final = false;
  private dirty = new Map<string, boolean>();
  private registrations = new Map<number, ValidationServiceRegistration>();
  private registrationIndex = 0;
  private handler: any | null = null;

  constructor(private validationProfiler: ValidationProfiler) {}

  initialize(validator: () => ValidationError[]) {
    this.validator = validator;
  }

  finalize() {
    if (!this.final) {
      this.final = true;
      this.validate();
    }
  }

  register(regValue: ValidationServiceRegistration): { regKey: number; dirty: boolean } {
    this.registrations.set(++this.registrationIndex, regValue);
    this.validate();
    return { regKey: this.registrationIndex, dirty: !!this.dirty.get(regValue.path) };
  }

  unregister(index: number, dirty: boolean) {
    const regValue = this.registrations.get(index);
    if (regValue) {
      this.registrations.delete(index);
      this.dirty.set(regValue.path, dirty);
    }
  }

  private doValidate() {
    let startFrom = new Date().getTime();
    const errors = new Map<string, ValidationError>();
    for (let error of this.validator()) {
      errors.set(error.path, error);
    }
    this.registrations.forEach((regValue: ValidationServiceRegistration, regKey: number) => {
      const result: ValidationError[] = [];
      if (regValue.pathExact) {
        const error = errors.get(regValue.path);
        if (error) {
          result.push(error);
        }
      } else {
        errors.forEach((errValue: ValidationError, errKey: string) => {
          if (errKey.startsWith(regValue.path)) {
            result.push(errValue);
          }
        });
      }
      regValue.consumer(this.final, result);
    });
    const executionTime = new Date().getTime() - startFrom;
    const subj = this.validationProfiler.getExecDurationSubj();
    if (subj !== null) {
      subj.next(executionTime);
    }
  }

  validate() {
    if (this.handler !== null) {
      clearTimeout(this.handler);
      this.handler = null;
    }
    this.handler = setTimeout(() => {
      this.handler = null;
      this.doValidate();
    }, 300);
  }

  navigate(element: HTMLInputElement | HTMLTextAreaElement, dir: boolean) {
    const elements = new Array<HTMLElement>();
    this.registrations.forEach((r) => {
      if (r.getElementForFocus) {
        const element = r.getElementForFocus();
        if (element) {
          elements.push(element);
        }
      }
    });
    elements.sort((a, b) => {
      const m = a.compareDocumentPosition(b);
      if (m & 2) {
        return 1;
      }
      if (m & 4) {
        return -1;
      }
      return 0;
    });
    const pos = elements.indexOf(element);
    if (pos < 0) {
      return;
    }
    const newPos = (pos + (dir ? 1 : -1) + elements.length) % elements.length;
    const newElement = elements[newPos];
    newElement.focus();
    newElement.click();
  }
}
